import React, { useState, useEffect } from 'react'
import AppBar from './AppBar'

interface PhotoDetailViewProps {
  images: string[]
  initialIndex: number
  onClose: () => void
  content?: string | null
}

const PhotoDetailView: React.FC<PhotoDetailViewProps> = ({
  images,
  initialIndex,
  onClose,
  content,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)

  useEffect(() => {
    setCurrentIndex(initialIndex)
  }, [initialIndex])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      } else if (e.key === 'ArrowLeft' && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1)
      } else if (e.key === 'ArrowRight' && currentIndex < images.length - 1) {
        setCurrentIndex(currentIndex + 1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentIndex, images.length, onClose])

  if (images.length === 0) return null

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black z-50 flex flex-col"
      // fixed라 immersive 상단 패딩을 못 받으므로 직접 안전영역만큼 밀어
      // 상단 뒤로가기(AppBar)가 노치 뒤로 숨지 않게 한다.
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <div className="app-container flex flex-col flex-1">
        <AppBar
          onBack={onClose}
          title="사진 자세히보기"
          variant="transparent"
          backIcon="/images/arrow-left-white.png"
        />

        {/* 이미지 영역 */}
        <div className="flex-1 flex items-center justify-center relative overflow-hidden">
          <img
            src={images[currentIndex]}
            alt={`사진 ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain"
          />

          {/* 이전 버튼 */}
          {currentIndex > 0 && (
            <button
              onClick={handlePrevious}
              className="absolute left-16px w-40px h-40px flex items-center justify-center bg-black bg-opacity-50 rounded-full"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-24px h-24px"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* 다음 버튼 */}
          {currentIndex < images.length - 1 && (
            <button
              onClick={handleNext}
              className="absolute right-16px w-40px h-40px flex items-center justify-center bg-black bg-opacity-50 rounded-full"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-24px h-24px"
              >
                <path
                  d="M9 18L15 12L9 6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* 리뷰 내용 */}
        {content && (
          <div className="px-16px py-16px bg-black bg-opacity-80 max-h-[200px] overflow-y-auto scrollbar-hide">
            <p className="text-style-body2 text-white whitespace-pre-wrap">
              {content}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default PhotoDetailView
